import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import {
  reportingPeriod,
  periodLabel,
  type MonthlyReport,
} from "@/lib/monthly-report";
import { ClipboardList, AlertCircle, CheckCircle2, FileText } from "lucide-react";

export const Route = createFileRoute("/manadsrapport")({
  head: () => ({
    meta: [
      { title: "Månadsrapport — Inkubera" },
      { name: "description", content: "Månadsrapport och TRL-utvärdering för dina bolag." },
    ],
  }),
  component: MonthlyReportList,
});

type Row = {
  company_id: string;
  company_name: string;
  report?: MonthlyReport;
};

function MonthlyReportList() {
  const { user } = useAuth();
  const period = useMemo(() => reportingPeriod(), []);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    (async () => {
      if (!user) return;
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      const admin = (roles ?? []).some((r) => r.role === "admin");
      setIsAdmin(admin);

      // Hämta bolag — coachen ser bara sina, admin ser alla
      let companyIds: string[] = [];
      if (admin) {
        const { data: comps } = await supabase
          .from("companies")
          .select("id, name")
          .eq("status", "Aktivt")
          .order("name");
        const list = (comps ?? []) as { id: string; name: string }[];
        companyIds = list.map((c) => c.id);
        const { data: reps } = await supabase
          .from("monthly_reports")
          .select("*")
          .eq("year", period.year)
          .eq("month", period.month)
          .in("company_id", companyIds);
        const repMap = new Map((reps ?? []).map((r) => [r.company_id, r as MonthlyReport]));
        setRows(list.map((c) => ({ company_id: c.id, company_name: c.name, report: repMap.get(c.id) })));
      } else {
        const { data: links } = await supabase
          .from("company_coaches")
          .select("company_id")
          .eq("coach_user_id", user.id);
        companyIds = (links ?? []).map((l) => l.company_id);
        if (companyIds.length === 0) {
          setRows([]);
          setLoading(false);
          return;
        }
        const { data: comps } = await supabase
          .from("companies")
          .select("id, name")
          .in("id", companyIds)
          .order("name");
        const list = (comps ?? []) as { id: string; name: string }[];
        const { data: reps } = await supabase
          .from("monthly_reports")
          .select("*")
          .eq("year", period.year)
          .eq("month", period.month)
          .in("company_id", companyIds);
        const repMap = new Map((reps ?? []).map((r) => [r.company_id, r as MonthlyReport]));
        setRows(list.map((c) => ({ company_id: c.id, company_name: c.name, report: repMap.get(c.id) })));
      }
      setLoading(false);
    })();
  }, [user, period.year, period.month]);

  const missing = rows.filter((r) => !r.report).length;

  return (
    <AppShell>
      <div className="px-8 py-10 max-w-5xl">
        <div className="flex items-center gap-3 mb-2">
          <ClipboardList className="size-6 text-muted-foreground" />
          <h1 className="font-display text-3xl">Månadsrapport</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-2 max-w-2xl">
          Rapportperiod: <strong>{periodLabel(period.year, period.month)}</strong>. Fyll i TRL-utveckling
          (CRL, BRL, TMRL, SRL) samt månadens framsteg, hinder, milstolpar och behov av stöd.
        </p>
        {isAdmin && (
          <p className="text-xs text-muted-foreground mb-6">Adminvy — visar alla aktiva bolag.</p>
        )}

        {!isAdmin && rows.length === 0 && !loading && (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Du är inte tilldelad något bolag ännu. Kontakta administratör.
          </div>
        )}

        {rows.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            <Stat label="Mina bolag" value={rows.length} />
            <Stat label="Inrapporterade" value={rows.length - missing} tone="ok" />
            <Stat label="Saknas" value={missing} tone={missing ? "warn" : "ok"} />
          </div>
        )}

        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left p-3">Bolag</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">TRL</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className="p-4 text-muted-foreground" colSpan={4}>
                    Laddar…
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.company_id} className="border-t border-border">
                    <td className="p-3 font-medium">{r.company_name}</td>
                    <td className="p-3">
                      {r.report ? (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-green-500/15 text-green-700 dark:text-green-300">
                          <CheckCircle2 className="size-3" /> Inrapporterad
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300">
                          <AlertCircle className="size-3" /> Saknas
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {r.report
                        ? `CRL ${r.report.crl ?? "—"} · BRL ${r.report.brl ?? "—"} · TMRL ${r.report.tmrl ?? "—"} · SRL ${r.report.srl ?? "—"}`
                        : "—"}
                    </td>
                    <td className="p-3 text-right">
                      <Link
                        to="/manadsrapport/$companyId"
                        params={{ companyId: r.company_id }}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-foreground text-background"
                      >
                        <FileText className="size-3" />
                        {r.report ? "Öppna" : "Fyll i"}
                      </Link>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "ok" | "warn" }) {
  const cls =
    tone === "warn"
      ? "text-amber-700 dark:text-amber-300"
      : tone === "ok"
        ? "text-green-700 dark:text-green-300"
        : "";
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`font-display text-3xl mt-1.5 ${cls}`}>{value}</div>
    </div>
  );
}
